/**
 * Volumenstudie in der 3D-Ansicht: Baukörper zeichnen (Rechteck aus drei Klicks, freies Polygon), auf Geschosse mal
 * Geschosshöhe hochziehen und live gegen das Grundstück rechnen — Grundfläche, BGF, GRZ, GFZ — und gegen das
 * zulässige Maß, das der Server aus Bebauungsplan oder Umgebung (§ 34) liefert (tri-server src/buildingModels/massing.ts).
 *
 * Alles hier ist rein: Koordinaten [lon, lat] rein, Zahlen und GeoJSON raus. Die Karte (Building3DView) zeichnet nur.
 */
import * as turf from "@turf/turf";

export type LonLat = [number, number];

export interface MassingBody {
  id: string;
  name: string;
  /** Grundriss, offen: der erste Punkt wird nicht wiederholt. */
  ring: LonLat[];
  storeys: number;
  storeyHeightM: number;
}

export interface MassingAllowed {
  basis: "BP" | "34" | "35" | "unklar";
  planName: string | null;
  reserveBasis: string | null;
  grz: { low: number; high: number } | null;
  gfz: { low: number; high: number } | null;
  storeys: { value: number; max?: number; source: "plan" | "umgebung" } | null;
  uses: string[];
  existing: { footprintM2: number; floorAreaM2: number; grz: number; gfz: number } | null;
}

export interface PlanFeature {
  kind: "baufenster" | "baugrenze" | "gruen" | "pflanzung" | "frei";
  label: string;
  geometry: any;
}

export interface MassingContext {
  parcel: { id: string; landSchl?: string; number: string | null; gemarkung: string | null; areaM2: number; geometry: any } | null;
  allowed: MassingAllowed | null;
  /** Baufenster, Baugrenzen und Grünfestsetzungen des Plans über dem Flurstück. */
  planFeatures?: PlanFeature[];
  /** Unversiegelt mindestens, aus der GRZ nach § 19 Abs. 4 BauNVO. */
  openSpaceMin?: { sealedShareMax: number; openShareMin: number; openM2Min: number } | null;
}

export const DEFAULT_STOREYS = 3;
export const DEFAULT_STOREY_HEIGHT_M = 3;

/** Meter je Grad am Ort — für kurze Strecken auf einem Grundstück genau genug. */
const metresPerDegree = (lat: number) => ({ x: 111_320 * Math.cos((lat * Math.PI) / 180), y: 110_540 });

const toLocal = (origin: LonLat, p: LonLat) => {
  const m = metresPerDegree(origin[1]);
  return { x: (p[0] - origin[0]) * m.x, y: (p[1] - origin[1]) * m.y };
};

const fromLocal = (origin: LonLat, x: number, y: number): LonLat => {
  const m = metresPerDegree(origin[1]);
  return [origin[0] + x / m.x, origin[1] + y / m.y];
};

/**
 * Rechteck aus drei Punkten: a–b ist eine Seite (etwa an der Straße entlang), c gibt die Tiefe — der Abstand von c
 * zur Linie a–b, auf der Seite, auf der c liegt.
 */
export const rectangleFrom = (a: LonLat, b: LonLat, c: LonLat): LonLat[] => {
  const B = toLocal(a, b);
  const C = toLocal(a, c);
  const length = Math.hypot(B.x, B.y);
  if (length < 0.01) return [a, b];
  const nx = -B.y / length;
  const ny = B.x / length;
  const depth = C.x * nx + C.y * ny;
  return [a, b, fromLocal(a, B.x + nx * depth, B.y + ny * depth), fromLocal(a, nx * depth, ny * depth)];
};

export const closed = (ring: LonLat[]): LonLat[] => (ring.length ? [...ring, ring[0]] : ring);

export const footprintM2 = (ring: LonLat[]): number => {
  if (ring.length < 3) return 0;
  try {
    return turf.area(turf.polygon([closed(ring)]));
  } catch {
    return 0;
  }
};

/** Kantenlängen in m, je Kante mit Mittelpunkt (für die Beschriftung auf der Karte). */
export const edges = (ring: LonLat[], close: boolean) => {
  const points = close && ring.length > 2 ? closed(ring) : ring;
  return points.slice(1).map((p, i) => {
    const q = points[i];
    return { lengthM: turf.distance(turf.point(q), turf.point(p), { units: "kilometers" }) * 1000, mid: [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2] as LonLat };
  });
};

export const bodyMetrics = (body: MassingBody) => {
  const footprint = footprintM2(body.ring);
  return { footprintM2: footprint, floorAreaM2: footprint * body.storeys, heightM: body.storeys * body.storeyHeightM };
};

export type Verdict = "ok" | "range" | "over" | "unknown";

/** Liegt ein Wert unter, in oder über dem zulässigen Maß (bei einer Spanne „range“ zwischen unterem und oberem Wert). */
export const verdictOf = (value: number, limit: { low: number; high: number } | null): Verdict => {
  if (!limit) return "unknown";
  if (value > limit.high + 1e-9) return "over";
  if (value > limit.low + 1e-9) return "range";
  return "ok";
};

/** Summen über alle Baukörper und ihr Verhältnis zum Grundstück und zum zulässigen Maß. */
export const totalsOf = (bodies: MassingBody[], context: MassingContext | null) => {
  const sums = bodies.map(bodyMetrics).reduce(
    (acc, m) => ({ footprintM2: acc.footprintM2 + m.footprintM2, floorAreaM2: acc.floorAreaM2 + m.floorAreaM2, heightM: Math.max(acc.heightM, m.heightM) }),
    { footprintM2: 0, floorAreaM2: 0, heightM: 0 },
  );
  const plot = context?.parcel?.areaM2 ?? 0;
  const grz = plot > 0 ? sums.footprintM2 / plot : null;
  const gfz = plot > 0 ? sums.floorAreaM2 / plot : null;
  const maxStoreys = bodies.reduce((m, b) => Math.max(m, b.storeys), 0);
  const allowed = context?.allowed ?? null;
  const storeyLimit = allowed?.storeys ? { low: allowed.storeys.value, high: allowed.storeys.max ?? allowed.storeys.value } : null;
  return {
    ...sums,
    plotM2: plot,
    grz,
    gfz,
    maxStoreys,
    verdict: {
      grz: grz === null ? "unknown" : verdictOf(grz, allowed?.grz ?? null),
      gfz: gfz === null ? "unknown" : verdictOf(gfz, allowed?.gfz ?? null),
      storeys: maxStoreys ? verdictOf(maxStoreys, storeyLimit) : "unknown",
    } as Record<"grz" | "gfz" | "storeys", Verdict>,
  };
};

/** Dicke der Decke zwischen zwei Geschossen in der Darstellung — sie macht die Geschosse lesbar. */
export const SLAB_M = 0.18;

/**
 * Die Baukörper als GeoJSON für die Extrusion der Karte: je Geschoss ein Körper (`storey`) und darüber seine Decke
 * (`slab`), dazu — wo das zulässige Maß mehr Vollgeschosse erlaubt als gezeichnet — die noch möglichen Geschosse
 * (`potential`) als durchscheinender Aufsatz.
 */
export const bodiesGeoJson = (bodies: MassingBody[], selectedId: string | null, allowedStoreys: number | null = null) => {
  const features: any[] = [];
  for (const b of bodies) {
    if (b.ring.length < 3) continue;
    const geometry = { type: "Polygon" as const, coordinates: [closed(b.ring)] };
    const selected = b.id === selectedId;
    const h = b.storeyHeightM;
    for (let i = 0; i < b.storeys; i++) {
      features.push({ type: "Feature", geometry, properties: { id: b.id, kind: "storey", selected, base: i * h, height: (i + 1) * h - SLAB_M } });
      features.push({ type: "Feature", geometry, properties: { id: b.id, kind: "slab", selected, base: (i + 1) * h - SLAB_M, height: (i + 1) * h } });
    }
    const extra = allowedStoreys !== null ? Math.max(0, Math.floor(allowedStoreys) - b.storeys) : 0;
    for (let i = 0; i < extra; i++) {
      const level = b.storeys + i;
      features.push({ type: "Feature", geometry, properties: { id: b.id, kind: "potential", selected, base: level * h, height: (level + 1) * h - SLAB_M } });
      features.push({ type: "Feature", geometry, properties: { id: b.id, kind: "potentialSlab", selected, base: (level + 1) * h - SLAB_M, height: (level + 1) * h } });
    }
  }
  return { type: "FeatureCollection" as const, features };
};

/** Grundrisse der Baukörper als Umriss am Boden. */
export const outlinesGeoJson = (bodies: MassingBody[], selectedId: string | null = null) => ({
  type: "FeatureCollection" as const,
  features: bodies
    .filter((b) => b.ring.length >= 3)
    .map((b) => ({ type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: closed(b.ring) }, properties: { id: b.id, selected: b.id === selectedId } })),
});

/**
 * Rechte Winkel einrasten: liegt die neue Kante von `last` nach `p` nahe an der Verlängerung oder an der Senkrechten
 * der vorigen Kante (`before` → `last`) — oder an einer der Achsen der ersten Kante (`axis`), damit auch die letzte
 * Kante rechtwinklig schließt —, wird `p` auf diese Richtung gezogen, mit gleicher Länge der Kante.
 */
export const snapToRightAngle = (
  before: LonLat,
  last: LonLat,
  p: LonLat,
  toleranceDeg = 12,
  axis: [LonLat, LonLat] | null = null,
): { point: LonLat; snapped: boolean } => {
  const prev = toLocal(last, before);
  const next = toLocal(last, p);
  const prevLength = Math.hypot(prev.x, prev.y);
  const length = Math.hypot(next.x, next.y);
  if (prevLength < 0.01 || length < 0.01) return { point: p, snapped: false };
  const directions: Array<[number, number]> = [];
  const addAxes = (dx: number, dy: number) => directions.push([dx, dy], [-dx, -dy], [-dy, dx], [dy, -dx]);
  addAxes(-prev.x / prevLength, -prev.y / prevLength);
  if (axis) {
    const a = toLocal(axis[0], axis[1]);
    const l = Math.hypot(a.x, a.y);
    if (l > 0.01) addAxes(a.x / l, a.y / l);
  }
  // Zurück auf die vorige Kante ist keine Kante.
  const back = [prev.x / prevLength, prev.y / prevLength];
  const limit = Math.cos((toleranceDeg * Math.PI) / 180);
  let best: { dx: number; dy: number; dot: number } | null = null;
  for (const [dx, dy] of directions) {
    if (dx * back[0] + dy * back[1] > 0.99) continue;
    const dot = (next.x * dx + next.y * dy) / length;
    if (dot > limit && (!best || dot > best.dot)) best = { dx, dy, dot };
  }
  if (!best) return { point: p, snapped: false };
  return { point: fromLocal(last, best.dx * length, best.dy * length), snapped: true };
};

/** Linie, Punkte und Kantenlängen des Grundrisses, der gerade gezeichnet wird (mit dem Punkt unter der Maus). */
export const draftGeoJson = (points: LonLat[], close: boolean) => ({
  line: {
    type: "FeatureCollection" as const,
    features:
      points.length >= 2
        ? [{ type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: close && points.length > 2 ? closed(points) : points }, properties: {} }]
        : [],
  },
  points: {
    type: "FeatureCollection" as const,
    features: points.map((p, i) => ({ type: "Feature" as const, geometry: { type: "Point" as const, coordinates: p }, properties: { first: i === 0 } })),
  },
  labels: {
    type: "FeatureCollection" as const,
    features: edges(points, close)
      .filter((e) => e.lengthM >= 0.5)
      .map((e) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: e.mid },
        properties: { label: `${e.lengthM.toLocaleString("de-DE", { maximumFractionDigits: 1 })} m` },
      })),
  },
});

export const newBodyId = () => `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Winkel an der Ecke `v` zwischen den Kanten nach `a` und nach `b`, in Grad. */
export const angleAt = (a: LonLat, v: LonLat, b: LonLat): number => {
  const p = toLocal(v, a);
  const q = toLocal(v, b);
  const lp = Math.hypot(p.x, p.y);
  const lq = Math.hypot(q.x, q.y);
  if (lp < 0.01 || lq < 0.01) return NaN;
  return (Math.acos(Math.max(-1, Math.min(1, (p.x * q.x + p.y * q.y) / (lp * lq)))) * 180) / Math.PI;
};

/** Die Ecken eines (geschlossen gedachten) Grundrisses mit rechtem Winkel — für die „90°“-Marken beim Zeichnen. */
export const rightAngleCorners = (ring: LonLat[], close: boolean, toleranceDeg = 0.5): LonLat[] => {
  const n = ring.length;
  const corners: LonLat[] = [];
  for (let i = 0; i < n; i++) {
    const hasPrev = close || i > 0;
    const hasNext = close || i < n - 1;
    if (!hasPrev || !hasNext || n < 3) continue;
    const angle = angleAt(ring[(i - 1 + n) % n], ring[i], ring[(i + 1) % n]);
    if (Math.abs(angle - 90) <= toleranceDeg) corners.push(ring[i]);
  }
  return corners;
};

/**
 * Den nächsten Punkt eines Polygons einrasten. Ab dem dritten Punkt zuerst die Ecke, an der sowohl die neue Kante als
 * auch die schließende Kante zum ersten Punkt rechtwinklig sind — so entsteht ein durchgehend rechtwinkliger
 * Grundriss —, sonst rechtwinklig oder in Verlängerung zur vorigen Kante bzw. zu den Achsen der ersten Kante.
 */
export const snapPolygonPoint = (points: LonLat[], raw: LonLat, toleranceDeg = 12): { point: LonLat; snapped: boolean } => {
  if (points.length < 2) return { point: raw, snapped: false };
  const last = points[points.length - 1];
  const before = points[points.length - 2];
  if (points.length >= 3) {
    const first = points[0];
    const origin = first;
    const L = toLocal(origin, last);
    const B = toLocal(origin, before);
    const F2 = toLocal(origin, points[1]);
    const R = toLocal(origin, raw);
    // Linie 1: durch `last`, senkrecht zur vorigen Kante; Linie 2: durch `first`, senkrecht zur ersten Kante.
    const d1 = { x: -(L.y - B.y), y: L.x - B.x };
    const d2 = { x: -F2.y, y: F2.x };
    const det = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(det) > 1e-6) {
      const t = ((0 - L.x) * d2.y - (0 - L.y) * d2.x) / det;
      const corner = { x: L.x + d1.x * t, y: L.y + d1.y * t };
      const edge = Math.hypot(corner.x - L.x, corner.y - L.y);
      const distance = Math.hypot(R.x - corner.x, R.y - corner.y);
      if (edge > 0.5 && distance <= Math.max(1, edge * Math.tan((toleranceDeg * Math.PI) / 180))) {
        return { point: fromLocal(origin, corner.x, corner.y), snapped: true };
      }
    }
  }
  return snapToRightAngle(before, last, raw, toleranceDeg, [points[0], points[1]]);
};

export const centroidOf = (ring: LonLat[]): LonLat => {
  const n = ring.length || 1;
  return [ring.reduce((s, p) => s + p[0], 0) / n, ring.reduce((s, p) => s + p[1], 0) / n];
};

/** Grundriss um `east` und `north` Meter verschieben. */
export const translateRing = (ring: LonLat[], east: number, north: number): LonLat[] => {
  if (!ring.length) return ring;
  const origin = ring[0];
  return ring.map((p) => {
    const l = toLocal(origin, p);
    return fromLocal(origin, l.x + east, l.y + north);
  });
};

/** Grundriss um seinen Schwerpunkt drehen, Grad gegen den Uhrzeigersinn. */
export const rotateRing = (ring: LonLat[], degrees: number): LonLat[] => {
  const c = centroidOf(ring);
  const r = (degrees * Math.PI) / 180;
  return ring.map((p) => {
    const l = toLocal(c, p);
    return fromLocal(c, l.x * Math.cos(r) - l.y * Math.sin(r), l.x * Math.sin(r) + l.y * Math.cos(r));
  });
};

/**
 * Grundrisse der 3D-Gebäude ([lon, lat, z]): die Bodenflächen, wo das Modell welche trägt, sonst die Dachflächen auf
 * den Boden projiziert — das Dach deckt den Grundriss, und viele LoD2-Modelle kommen ohne Bodenfläche.
 */
export const footprintsFromSurfaces = (buildings: Array<{ surfaces: Array<{ type?: string; rings: number[][][] }> }>): LonLat[][] =>
  buildings.flatMap((b) => {
    const pick = (type: string) =>
      (b.surfaces ?? []).filter((s) => s.type === type && s.rings?.[0]?.length >= 3).map((s) => s.rings[0].map((v) => [v[0], v[1]] as LonLat));
    const ground = pick("GroundSurface");
    return ground.length ? ground : pick("RoofSurface");
  });

/**
 * Freifläche des Grundstücks: das Flurstück ohne die Grundrisse der Gebäude (Bestand) und der gezeichneten
 * Baukörper. Ergebnis als GeoJSON und in m²; ohne Flurstück null.
 */
export const openSpaceOf = (plot: any, footprints: LonLat[][]): { geometry: any; areaM2: number } | null => {
  if (!plot?.type) return null;
  let rest: any = turf.feature(plot);
  const [minX, minY, maxX, maxY] = turf.bbox(rest);
  for (const ring of footprints) {
    if (ring.length < 3) continue;
    if (ring.every((p) => p[0] < minX || p[0] > maxX || p[1] < minY || p[1] > maxY)) continue;
    try {
      const cut = turf.difference(turf.featureCollection([rest, turf.polygon([closed(ring)])]));
      if (!cut) return { geometry: null, areaM2: 0 };
      rest = cut;
    } catch {
      // Ein unsauberer Grundriss (Selbstschnitt) bleibt außen vor, statt die ganze Rechnung zu kippen.
    }
  }
  return { geometry: rest.geometry, areaM2: turf.area(rest) };
};

/**
 * Abstandsflächen nach Landesbauordnung, vereinfacht: Tiefe = Faktor × Wandhöhe, mindestens `minM`; in Gewerbe- und
 * Industriegebieten meist der kleinere Faktor. Nach dem Ländercode (erste zwei Ziffern des Flurstückskennzeichens).
 * Einschätzung — Sonderregeln (Dachneigung, Giebel, Schmalseitenprivileg, geschlossene Bauweise, Hälfte der
 * öffentlichen Verkehrsfläche) sind nicht abgebildet.
 */
export const SETBACK_RULES: Record<string, { factor: number; industrialFactor: number; minM: number; law: string }> = {
  "01": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 LBO SH" },
  "02": { factor: 0.4, industrialFactor: 0.2, minM: 2.5, law: "§ 6 HBauO" },
  "03": { factor: 0.5, industrialFactor: 0.25, minM: 3, law: "§ 5 NBauO" },
  "04": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 BremLBO" },
  "05": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 BauO NRW" },
  "06": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 HBO" },
  "07": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 8 LBauO RP" },
  "08": { factor: 0.4, industrialFactor: 0.2, minM: 2.5, law: "§ 5 LBO BW" },
  "09": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "Art. 6 BayBO" },
  "10": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 7 LBO SL" },
  "11": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 BauO Bln" },
  "12": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 BbgBO" },
  "13": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 LBauO M-V" },
  "14": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 SächsBO" },
  "15": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 BauO LSA" },
  "16": { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 ThürBO" },
};
const MBO_RULE = { factor: 0.4, industrialFactor: 0.2, minM: 3, law: "§ 6 MBO" };

export const setbackRuleFor = (parcelId: string | null | undefined, uses: string[] = []) => {
  const rule = SETBACK_RULES[String(parcelId ?? "").slice(0, 2)] ?? MBO_RULE;
  const industrial = uses.length > 0 && uses.every((u) => /^(GE|GI)/.test(u));
  return { factor: industrial ? rule.industrialFactor : rule.factor, minM: rule.minM, law: rule.law, industrial };
};

export const setbackDepthM = (heightM: number, rule: { factor: number; minM: number }) => Math.max(rule.minM, rule.factor * heightM);

/**
 * Die Abstandsfläche eines Grundrisses: vor jeder Wand ein Rechteck in der Tiefe `depthM`, nach außen. Ergebnis als
 * Liste von Ringen (für die gestrichelte Linie) und als Vereinigung (für den Abgleich mit dem Grundstück).
 */
export const setbackOf = (ring: LonLat[], depthM: number): { rects: LonLat[][]; union: any | null } => {
  if (ring.length < 3 || depthM <= 0) return { rects: [], union: null };
  const origin = ring[0];
  const local = ring.map((p) => toLocal(origin, p));
  // Umlaufsinn: positiv = gegen den Uhrzeigersinn; die Außennormale einer Kante zeigt dann nach rechts.
  const signed = local.reduce((sum, p, i) => {
    const q = local[(i + 1) % local.length];
    return sum + (p.x * q.y - q.x * p.y);
  }, 0);
  const sign = signed > 0 ? 1 : -1;
  const rects: LonLat[][] = [];
  for (let i = 0; i < local.length; i++) {
    const p = local[i];
    const q = local[(i + 1) % local.length];
    const length = Math.hypot(q.x - p.x, q.y - p.y);
    if (length < 0.01) continue;
    const nx = (sign * (q.y - p.y)) / length;
    const ny = (sign * -(q.x - p.x)) / length;
    rects.push([
      fromLocal(origin, p.x, p.y),
      fromLocal(origin, q.x, q.y),
      fromLocal(origin, q.x + nx * depthM, q.y + ny * depthM),
      fromLocal(origin, p.x + nx * depthM, p.y + ny * depthM),
    ]);
  }
  let union: any = null;
  try {
    const features = rects.map((r) => turf.polygon([closed(r)]));
    union = features.length > 1 ? turf.union(turf.featureCollection(features)) : features[0] ?? null;
  } catch {
    union = null;
  }
  return { rects, union };
};

/** Was von der Abstandsfläche über das Grundstück hinausragt (GeoJSON und m²). */
export const setbackOutside = (setback: any | null, plot: any | null): { geometry: any; areaM2: number } | null => {
  if (!setback || !plot?.type) return null;
  try {
    const outside = turf.difference(turf.featureCollection([setback, turf.feature(plot)]));
    return outside ? { geometry: outside.geometry, areaM2: turf.area(outside) } : { geometry: null, areaM2: 0 };
  } catch {
    return null;
  }
};
